import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ShieldCheck, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { roleLabelTh } from '@/lib/role-label';
import { usersListQueryOptions } from '@/lib/users';

const PREVIEW_LIMIT = 5;

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชั่วโมงที่แล้ว`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} วันที่แล้ว`;
  return new Date(iso).toLocaleDateString('th-TH', { month: 'short', day: 'numeric' });
}

/**
 * Dashboard widget for admins with `user.update`. Surfaces the 5 most
 * recently-created users — useful for the security check "is there an
 * account I don't recognize?" and to keep an eye on invited members
 * who haven't activated yet (status=invited / pending).
 *
 * Gated by the parent route's permission check so we don't need to
 * read session here. Uses the existing users list endpoint (sorted
 * by createdAt desc on the backend); no new API surface needed.
 */
export function RecentUsersCard() {
  const query = useQuery(usersListQueryOptions({ perPage: PREVIEW_LIMIT }));
  const items = query.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserPlus className="h-4 w-4 text-slate-400" />
          ผู้ใช้ที่เพิ่งเข้าร่วม
        </CardTitle>
        <Link to="/users" className="text-xs text-blue-600 hover:underline dark:text-blue-400">
          จัดการ →
        </Link>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400">ยังไม่มีผู้ใช้</p>
        ) : (
          <ul className="space-y-2 text-xs">
            {items.map((u) => (
              <li
                key={u.id}
                className="flex items-baseline justify-between gap-2 border-b border-slate-100 pb-1.5 last:border-0 dark:border-slate-800"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                    {u.name}
                    {u.twoFactorEnabled && (
                      <ShieldCheck
                        className="h-3 w-3 shrink-0 text-emerald-500"
                        aria-label="2FA enabled"
                      />
                    )}
                  </div>
                  <div className="truncate font-mono text-[11px] text-slate-400">
                    {u.email} · {roleLabelTh(u.role.slug)}
                  </div>
                </div>
                <span className="shrink-0 text-[11px] text-slate-400">
                  {formatRelative(u.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
